import { Children, useEffect, useState } from 'react';
import { TabList } from './TabList';
import { TabPanel } from './TabPanel';

/**
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode[]} props.children - The tab panels or tab items to render.
 * @param {number} [props.initialTab=0] - The index of the tab that should be active by default.
 * @param {(index: number) => void} [props.onChangeTab=() => {}] - Callback fired when the active tab changes.
 * @param {Object} [props.style={}] - Custom styles applied to the root container of the Tabs component.
 * @param {Object} [props.tabPanelStyle={}] - Custom styles applied to the tab panel container.
 * @param {Object} [props.tabListStyle={}] - Custom styles applied to the tab list container.
 * @param {'border' | 'background'} [props.variant='border'] - The visual style used to highlight the active tab.
 */
export const Tabs = ({
  children,
  initialTab = 0,
  onChangeTab = () => {},
  style = {},
  tabPanelStyle = {},
  tabListStyle = {},
  variant = 'border',
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    onChangeTab(activeTab);
  }, [activeTab, onChangeTab]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...style,
      }}
    >
      {Children.map(children, (child) => {
        if (child.type.displayName === 'TabList') {
          return (
            <TabList setActiveTab={setActiveTab} activeTab={activeTab} style={tabListStyle} variant={variant}>
              {child.props.children}
            </TabList>
          );
        }
        return null;
      })}
      {Children.map(children, (child) => {
        if (child.type.displayName === 'TabPanel') {
          return (
            <TabPanel key={activeTab} style={tabPanelStyle}>
              {child.props.children?.length
                ? child.props.children[activeTab]
                : child.props.children}
            </TabPanel>
          );
        }
        return null;
      })}
    </div>
  );
};
